import { useState, useEffect, useMemo, useRef } from 'react';
import { Icon } from 'leaflet';
import { LatLngExpression } from 'leaflet';
import { TelemetryPoint, computeStressAtIndex, haversineMeters, bearingDeg, angleDiffDeg } from '../utils/telemetryUtils';
import {
  FlightSegmentType,
  ColoredFlightPolyline,
  FlightMovementMarker,
  FlightHeuristicThresholds,
  classifyFlightSegment,
  flightSegmentColor,
  makeFlightMovementDivIcon,
} from '../utils/flightHeuristics';
import { FlightTrailEvent, RunSplitEvent, UnitSystem } from '../types/event';
import { metersToFeet } from '../utils/formatters';

export interface UseFlightAnalysisResult {
  verticalSpeedMps: number | null;
  flightPhase: string | null;
  headingDegNow: number | null;
  flightTrailEvents: FlightTrailEvent[];
  flightMovementMarkers: FlightMovementMarker[];
  flightColoredPolylines: ColoredFlightPolyline[];
  runSplits: RunSplitEvent[];
  markerIcon: Icon;
  distance: number;
}

export const useFlightAnalysis = (
  telemetryPoints: TelemetryPoint[],
  eventType: string | undefined,
  flightThr: FlightHeuristicThresholds,
  hashId: string | undefined,
  unitSystem: UnitSystem,
  athleteInitial: string = 'A',
): UseFlightAnalysisResult => {
  const [verticalSpeedMps, setVerticalSpeedMps] = useState<number | null>(null);
  const [flightPhase, setFlightPhase] = useState<string | null>(null);
  const [headingDegNow, setHeadingDegNow] = useState<number | null>(null);
  const [flightTrailEvents, setFlightTrailEvents] = useState<FlightTrailEvent[]>([]);
  const [flightMovementMarkers, setFlightMovementMarkers] = useState<FlightMovementMarker[]>([]);
  const [distance, setDistance] = useState(0);

  // Refs for incremental trail accumulation — prevents sliding-window data loss.
  const processedTrailIndexRef = useRef(0);
  const trailLastByActionRef = useRef(new Map<string, number>());
  const engineOnDetectedRef = useRef(false);
  const lastCircuitIdRef = useRef<string | null>(null);

  // Refs for incremental marker accumulation (same pattern as trail).
  const processedMarkersIndexRef = useRef(0);
  const markersLastByActionRef = useRef(new Map<string, number>());

  // Reset all incremental state whenever we navigate to a different event.
  useEffect(() => {
    setFlightTrailEvents([]);
    processedTrailIndexRef.current = 0;
    trailLastByActionRef.current = new Map();
    engineOnDetectedRef.current = false;
    lastCircuitIdRef.current = null;
    setFlightMovementMarkers([]);
    processedMarkersIndexRef.current = 0;
    markersLastByActionRef.current = new Map();
  }, [hashId]);

  // Distance calculation
  useEffect(() => {
    if (telemetryPoints.length < 2) return;
    let totalDistance = 0;
    for (let i = 1; i < telemetryPoints.length; i++) {
      totalDistance += haversineMeters(telemetryPoints[i - 1], telemetryPoints[i]);
    }
    setDistance(totalDistance / 1000);
  }, [telemetryPoints]);

  const flightColoredPolylines = useMemo<ColoredFlightPolyline[]>(() => {
    if (eventType !== 'flight') return [];
    if (telemetryPoints.length < 2) return [];

    const maxPoints = 900;
    const pts = telemetryPoints.slice(Math.max(0, telemetryPoints.length - maxPoints));

    const polylines: ColoredFlightPolyline[] = [];
    let currentType: FlightSegmentType | null = null;
    let currentPositions: LatLngExpression[] = [];

    const flush = () => {
      if (!currentType || currentPositions.length < 2) return;
      polylines.push({
        id: `flight-poly-${polylines.length}-${currentType}`,
        type: currentType,
        color: flightSegmentColor(currentType),
        positions: currentPositions,
      });
    };

    currentPositions = [[pts[0].latitude, pts[0].longitude]];

    for (let i = 1; i < pts.length; i++) {
      const p1 = pts[i - 1];
      const p2 = pts[i];
      const p0 = i >= 2 ? pts[i - 2] : null;

      const t2 = Date.parse(p2.timestamp);
      const t1 = Date.parse(p1.timestamp);
      const dt = (t2 - t1) / 1000;
      if (!Number.isFinite(dt) || dt <= 0.5 || dt > 60) continue;

      const seg = classifyFlightSegment(p0, p1, p2, dt, flightThr);
      const segType = seg.type;

      if (currentType === null) {
        currentType = segType;
        currentPositions = [[p1.latitude, p1.longitude], [p2.latitude, p2.longitude]];
        continue;
      }

      if (segType === currentType) {
        currentPositions.push([p2.latitude, p2.longitude]);
      } else {
        flush();
        currentType = segType;
        currentPositions = [[p1.latitude, p1.longitude], [p2.latitude, p2.longitude]];
      }
    }

    flush();
    return polylines;
  }, [telemetryPoints, eventType, flightThr]);

  // Incremental map marker accumulation — mirrors the trail ref pattern so that
  // early markers (taxi roll, takeoff run, initial climb) are never lost as
  // new telemetry streams in.
  useEffect(() => {
    if (eventType !== 'flight') {
      setFlightMovementMarkers([]);
      processedMarkersIndexRef.current = 0;
      markersLastByActionRef.current = new Map();
      return;
    }
    if (telemetryPoints.length < 3) return;

    if (processedMarkersIndexRef.current > telemetryPoints.length) {
      setFlightMovementMarkers([]);
      processedMarkersIndexRef.current = 0;
      markersLastByActionRef.current = new Map();
    }

    const startIdx = Math.max(2, processedMarkersIndexRef.current);
    const newMarkers: FlightMovementMarker[] = [];

    for (let i = startIdx; i < telemetryPoints.length; i++) {
      const p0 = telemetryPoints[i - 2];
      const p1 = telemetryPoints[i - 1];
      const p2 = telemetryPoints[i];

      const t2 = Date.parse(p2.timestamp);
      const t1 = Date.parse(p1.timestamp);
      const dt = (t2 - t1) / 1000;
      if (!Number.isFinite(dt) || dt <= 0.5 || dt > 60) continue;

      const seg = classifyFlightSegment(p0, p1, p2, dt, flightThr);
      if (seg.type === 'straight') continue;

      const { stress } = computeStressAtIndex(telemetryPoints, i);
      const altM = typeof p2.altitude === 'number' ? p2.altitude : null;
      const spdKmh = typeof p2.speed === 'number' ? p2.speed : null;

      let action = '';
      let glyph = '';
      if (seg.type === 'climb') {
        action = 'Climbing';
        glyph = '▲';
      } else if (seg.type === 'sink') {
        action = 'Sinking';
        glyph = '▼';
      } else {
        action = `Turning ${seg.turnDir ?? ''}`.trim();
        glyph = seg.turnDir === 'Left' ? '↶' : '↷';
      }

      const minSpacingSeconds = seg.type === 'turn' ? 10 : 15;
      const lastSeen = markersLastByActionRef.current.get(action);
      if (typeof lastSeen === 'number' && (t2 - lastSeen) / 1000 < minSpacingSeconds) continue;
      markersLastByActionRef.current.set(action, t2);

      newMarkers.push({
        id: `flight-move-${seg.type}-${i}-${p2.timestamp}`,
        timestamp: p2.timestamp,
        position: [p2.latitude, p2.longitude],
        icon: makeFlightMovementDivIcon(glyph, stress),
        action,
        altM,
        spdKmh,
      });
    }

    processedMarkersIndexRef.current = telemetryPoints.length;

    if (newMarkers.length > 0) {
      setFlightMovementMarkers(prev => {
        const combined = [...prev, ...newMarkers];
        return combined.length > 300 ? combined.slice(-300) : combined;
      });
    }
  }, [telemetryPoints, eventType, flightThr]);

  const runSplits = useMemo<RunSplitEvent[]>(() => {
    if (eventType !== 'run') return [];
    if (telemetryPoints.length < 2) return [];

    let totalM = 0;
    let nextKmM = 1000;
    let hrSum = 0;
    let hrCount = 0;
    const splits: RunSplitEvent[] = [];

    for (let i = 1; i < telemetryPoints.length; i++) {
      const prev = telemetryPoints[i - 1];
      const curr = telemetryPoints[i];
      totalM += haversineMeters(prev, curr);

      if (typeof curr.heart_rate === 'number' && curr.heart_rate > 0) {
        hrSum += curr.heart_rate;
        hrCount += 1;
      }

      while (totalM >= nextKmM) {
        const km = nextKmM / 1000;
        const avg = hrCount > 0 ? Math.round(hrSum / hrCount) : null;
        splits.push({
          id: `run-split-${km}-${curr.timestamp}`,
          timestamp: curr.timestamp,
          km,
          avgHeartRateBpm: avg,
          position: [curr.latitude, curr.longitude],
        });
        hrSum = 0;
        hrCount = 0;
        nextKmM += 1000;
      }
    }

    return splits.slice(-60);
  }, [telemetryPoints, eventType]);

  const markerIcon = useMemo(() => {
    const letter = athleteInitial || (eventType === 'flight' ? 'P' : 'A');
    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
  <defs>
    <filter id="shadow" x="-25%" y="-20%" width="150%" height="150%">
      <feDropShadow dx="0" dy="1.5" stdDeviation="2" flood-opacity="0.35"/>
    </filter>
    <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#3b82f6"/>
      <stop offset="100%" stop-color="#1d4ed8"/>
    </linearGradient>
  </defs>
  <g filter="url(#shadow)">
    <circle cx="20" cy="20" r="16" fill="url(#bg)"/>
    <circle cx="20" cy="20" r="14.5" fill="none" stroke="#ffffff" stroke-opacity="0.3" stroke-width="1"/>
    <text x="20" y="20" text-anchor="middle" dominant-baseline="central"
          fill="#ffffff" font-family="system-ui,-apple-system,sans-serif"
          font-size="16" font-weight="700" letter-spacing="0.5">${letter}</text>
  </g>
</svg>`;
    const iconUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

    return new Icon({
      iconUrl,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20],
    });
  }, [eventType, athleteInitial]);

  // Flight analysis: vertical speed, phase, heading, and incremental trail accumulation.
  useEffect(() => {
    if (eventType !== 'flight') {
      setVerticalSpeedMps(null);
      setFlightPhase(null);
      setHeadingDegNow(null);
      setFlightTrailEvents([]);
      return;
    }

    if (telemetryPoints.length < 2) return;

    const last = telemetryPoints[telemetryPoints.length - 1];
    const prev = telemetryPoints[telemetryPoints.length - 2];

    setHeadingDegNow(bearingDeg(prev, last));

    const t2 = Date.parse(last.timestamp);
    const t1 = Date.parse(prev.timestamp);
    const dt = (t2 - t1) / 1000;

    let vsLocal: number | null = null;
    if (
      Number.isFinite(dt) && dt > 0.5 && dt < 60 &&
      typeof last.altitude === 'number' && typeof prev.altitude === 'number'
    ) {
      const vs = (last.altitude - prev.altitude) / dt;
      vsLocal = Math.max(-60, Math.min(60, vs));
    }

    setVerticalSpeedMps(vsLocal);

    const speedKmh = typeof last.speed === 'number' ? last.speed : 0;
    const altM = typeof last.altitude === 'number' ? last.altitude : null;
    const vsAbs = vsLocal !== null ? Math.abs(vsLocal) : null;

    if (speedKmh < 60 && (vsAbs === null || vsAbs < 0.7) && (altM === null || altM < 300)) {
      setFlightPhase('Taxiing / Ground');
    } else {
      setFlightPhase('Flying');
    }

    // --- Incremental trail accumulation ---
    if (processedTrailIndexRef.current > telemetryPoints.length) {
      setFlightTrailEvents([]);
      processedTrailIndexRef.current = 0;
      trailLastByActionRef.current = new Map();
      engineOnDetectedRef.current = false;
      lastCircuitIdRef.current = null;
    }

    const startIdx = Math.max(1, processedTrailIndexRef.current);
    const newTrailEvents: FlightTrailEvent[] = [];

    const pushIfSpaced = (e: FlightTrailEvent, minSpacingSeconds: number) => {
      const t = Date.parse(e.timestamp);
      if (!Number.isFinite(t)) return;
      const lastMs = trailLastByActionRef.current.get(e.action);
      if (typeof lastMs === 'number' && (t - lastMs) / 1000 < minSpacingSeconds) return;
      trailLastByActionRef.current.set(e.action, t);
      newTrailEvents.push(e);
    };

    for (let i = startIdx; i < telemetryPoints.length; i++) {
      const p1 = telemetryPoints[i - 1];
      const p2 = telemetryPoints[i];
      const ti2 = Date.parse(p2.timestamp);
      const ti1 = Date.parse(p1.timestamp);
      const dti = (ti2 - ti1) / 1000;
      if (!Number.isFinite(dti) || dti <= 0.5 || dti > 60) continue;

      const { stress, delta } = computeStressAtIndex(telemetryPoints, i);
      const approxAltitudeM = typeof p2.altitude === 'number' ? p2.altitude : undefined;
      const approxSpeedKmh = typeof p2.speed === 'number' ? p2.speed : undefined;
      const detail = delta !== undefined ? `ΔHR ${delta > 0 ? '+' : ''}${delta}` : undefined;

      // Climb / Descent
      if (typeof p2.altitude === 'number' && typeof p1.altitude === 'number') {
        const vs = (p2.altitude - p1.altitude) / dti;
        if (vs >= 2.0) {
          pushIfSpaced({ id: `trail-climb-${i}-${p2.timestamp}`, timestamp: p2.timestamp, action: 'Climbing', stress, approxAltitudeM, approxSpeedKmh, detail }, 15);
        } else if (vs <= -2.0) {
          pushIfSpaced({ id: `trail-desc-${i}-${p2.timestamp}`, timestamp: p2.timestamp, action: 'Descending', stress, approxAltitudeM, approxSpeedKmh, detail }, 15);
        }
      }

      // Turns with direction
      if (i >= 2) {
        const p0 = telemetryPoints[i - 2];
        const h1 = bearingDeg(p0, p1);
        const h2 = bearingDeg(p1, p2);
        const signedDelta = angleDiffDeg(h1, h2);
        const rate = Math.abs(signedDelta) / dti;
        const speedHere = typeof p2.speed === 'number' ? p2.speed : 0;
        if (speedHere >= 80 && rate >= 3) {
          const dir = signedDelta >= 0 ? 'Right' : 'Left';
          pushIfSpaced({ id: `trail-turn-${i}-${p2.timestamp}`, timestamp: p2.timestamp, action: `Turning ${dir}`, stress, approxAltitudeM, approxSpeedKmh, detail }, 10);
        }
      }

      // Taxiing: moving on ground (speed 5–60 km/h, low altitude, minimal vertical speed)
      const taxiSpeedKmh = typeof p2.speed === 'number' ? p2.speed : 0;
      const taxiAltM = typeof p2.altitude === 'number' ? p2.altitude : null;
      const taxiVs = typeof p2.altitude === 'number' && typeof p1.altitude === 'number'
        ? Math.abs((p2.altitude - p1.altitude) / dti)
        : null;
      if (
        taxiSpeedKmh > 5 &&
        taxiSpeedKmh < 60 &&
        (taxiAltM === null || taxiAltM < 300) &&
        (taxiVs === null || taxiVs < 0.7)
      ) {
        pushIfSpaced({ id: `trail-taxi-${i}-${p2.timestamp}`, timestamp: p2.timestamp, action: 'Taxiing', stress, approxAltitudeM, approxSpeedKmh, detail }, 60);
      }
    }

    processedTrailIndexRef.current = telemetryPoints.length;

    // Engine-on heuristic — detect once per session (earliest event in the trail)
    const detectEngineOn = (): FlightTrailEvent | null => {
      if (telemetryPoints.length < 6) return null;
      const earlyWindow = telemetryPoints.slice(0, Math.min(telemetryPoints.length, 240));
      if (earlyWindow.length < 6) return null;

      const tStart = Date.parse(earlyWindow[0].timestamp);
      if (!Number.isFinite(tStart)) return null;

      let sustainedSeconds = 0;
      for (let i = 1; i < earlyWindow.length; i++) {
        const p1 = earlyWindow[i - 1];
        const p2 = earlyWindow[i];
        const t2 = Date.parse(p2.timestamp);
        const t1 = Date.parse(p1.timestamp);
        const dti = (t2 - t1) / 1000;
        if (!Number.isFinite(dti) || dti <= 0.5 || dti > 60) continue;
        if ((t2 - tStart) / 1000 > flightThr.engineWindowSec) break;

        const speedKmh = typeof p2.speed === 'number' ? p2.speed : 0;
        const altM = typeof p2.altitude === 'number' ? p2.altitude : null;
        const lowAlt = altM === null ? true : altM < flightThr.engineAltMaxM;
        if (lowAlt && speedKmh >= flightThr.engineSpeedMinKmh) {
          sustainedSeconds += dti;
        } else {
          sustainedSeconds = 0;
        }

        if (sustainedSeconds >= flightThr.engineSustainSec) {
          const { stress } = computeStressAtIndex(earlyWindow, i);
          return {
            id: `trail-engine-${p2.timestamp}`,
            timestamp: p2.timestamp,
            action: 'Engine on',
            stress,
            approxAltitudeM: typeof p2.altitude === 'number' ? p2.altitude : undefined,
            approxSpeedKmh: typeof p2.speed === 'number' ? p2.speed : undefined,
          };
        }
      }
      return null;
    };

    if (!engineOnDetectedRef.current) {
      const engineEvent = detectEngineOn();
      if (engineEvent) {
        engineOnDetectedRef.current = true;
        newTrailEvents.unshift(engineEvent);
      }
    }

    // Circuit heuristic — re-detect every update; deduplicate by id.
    const detectCircuit = (): FlightTrailEvent | null => {
      if (telemetryPoints.length < 20) return null;
      const recent = telemetryPoints.slice(Math.max(0, telemetryPoints.length - 420));
      const low = recent.filter((p) => typeof p.altitude === 'number' && p.altitude < flightThr.circuitAltMaxM);
      if (low.length < flightThr.circuitMinLowPoints) return null;

      const start = low[0];
      const end = low[low.length - 1];
      const loopCloseM = haversineMeters(start, end);
      if (loopCloseM > flightThr.circuitCloseMeters) return null;

      let minLat = Number.POSITIVE_INFINITY;
      let maxLat = Number.NEGATIVE_INFINITY;
      let minLon = Number.POSITIVE_INFINITY;
      let maxLon = Number.NEGATIVE_INFINITY;
      for (const p of low) {
        minLat = Math.min(minLat, p.latitude);
        maxLat = Math.max(maxLat, p.latitude);
        minLon = Math.min(minLon, p.longitude);
        maxLon = Math.max(maxLon, p.longitude);
      }

      const widthM = haversineMeters({ ...start, latitude: minLat, longitude: minLon }, { ...start, latitude: minLat, longitude: maxLon });
      const heightM = haversineMeters({ ...start, latitude: minLat, longitude: minLon }, { ...start, latitude: maxLat, longitude: minLon });
      if (widthM > flightThr.circuitBoxMaxMeters || heightM > flightThr.circuitBoxMaxMeters) return null;

      let pathM = 0;
      for (let i = 1; i < low.length; i++) {
        pathM += haversineMeters(low[i - 1], low[i]);
      }
      if (pathM < flightThr.circuitMinPathMeters) return null;

      const { stress } = computeStressAtIndex(recent, Math.max(0, recent.length - 1));
      const thresholdDisplay = unitSystem === 'imperial'
        ? `${Math.round(metersToFeet(flightThr.circuitAltMaxM))} ft`
        : `${flightThr.circuitAltMaxM} m`;
      const pathDisplay = unitSystem === 'imperial'
        ? `${Math.round(metersToFeet(pathM))} ft`
        : `${Math.round(pathM)} m`;

      return {
        id: `trail-circuit-${end.timestamp}`,
        timestamp: end.timestamp,
        action: 'Circuit detected',
        stress,
        approxAltitudeM: typeof end.altitude === 'number' ? end.altitude : undefined,
        approxSpeedKmh: typeof end.speed === 'number' ? end.speed : undefined,
        detail: `Low (<${thresholdDisplay}) loop ~${pathDisplay}`,
      };
    };

    const circuitEvent = detectCircuit();
    if (circuitEvent && circuitEvent.id !== lastCircuitIdRef.current) {
      lastCircuitIdRef.current = circuitEvent.id;
      newTrailEvents.push(circuitEvent);
    }

    if (newTrailEvents.length > 0) {
      setFlightTrailEvents(prev => {
        const combined = [...prev, ...newTrailEvents];
        return combined.length > 300 ? combined.slice(-300) : combined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telemetryPoints, eventType, flightThr]);

  return {
    verticalSpeedMps,
    flightPhase,
    headingDegNow,
    flightTrailEvents,
    flightMovementMarkers,
    flightColoredPolylines,
    runSplits,
    markerIcon,
    distance,
  };
};
